import { parseStartParams } from "@/utils/urlEncoder";
import { ANALYTICS_SOURCE, STORAGE_KEYS } from "./config";
import { session } from "./storage";

/**
 * A launch may override the envelope `source` by carrying `?source=…` — the
 * app-center listing registers its URL as `…/?source=appcenter`, so events from
 * the directory are separable from the same build opened anywhere else.
 *
 * Normalised to the charset the backend accepts and capped, so a hand-edited
 * URL cannot push arbitrary text into the envelope and trip a 422.
 */
const normalise = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const clean = value.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
  return clean ? clean.slice(0, 32) : undefined;
};

const readLaunchSource = (): string | undefined => {
  if (typeof window === "undefined") return undefined;

  const fromUrl = normalise(
    new URLSearchParams(window.location.search).get("source"),
  );
  if (fromUrl) return fromUrl;

  // Same fallback the UTMs use: Eitaa deep links carry their query inside the
  // base64url `start_param` rather than the address bar.
  return normalise(parseStartParams().source);
};

/**
 * Resolve this launch's `source` once, before in-app navigation rewrites the
 * URL, and freeze it for the session. Call from `initAnalytics`.
 *
 * A launch with no `?source=` clears any value left by a previous launch, so
 * the override never outlives the launch that set it.
 */
export const captureLaunchSource = (): void => {
  if (typeof window === "undefined") return;

  const launchSource = readLaunchSource();
  if (launchSource) session.set(STORAGE_KEYS.source, launchSource);
  else session.remove(STORAGE_KEYS.source);
};

/** The envelope `source` for this launch — the URL override, else the build constant. */
export const getSource = (): string =>
  session.get(STORAGE_KEYS.source) ?? ANALYTICS_SOURCE;
